import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useWhatsAppNumber, buildWaLink } from "@/lib/whatsapp";
import { Check, Leaf, MessageCircle, Phone, ArrowLeft } from "lucide-react";

export default function ProductDetail() {
  const { id } = useParams();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const number = useWhatsAppNumber();

  useEffect(() => {
    setLoading(true);
    api.get(`/products/${id}`)
      .then((r) => setProduct(r.data))
      .catch(() => setError("Product not found"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="container-x py-32 text-center text-oshveda-muted" data-testid="product-loading">Loading…</div>;
  if (error || !product) return (
    <div className="container-x py-32 text-center" data-testid="product-not-found">
      <p className="text-oshveda-muted mb-4">{error}</p>
      <Link to="/shop" className="text-oshveda-accent underline">Back to shop</Link>
    </div>
  );

  const waMessage = `Hello Oshveda Naturals, I'd like to order:\n\nProduct: ${product.name}\nPrice: ₹${product.price}\nSize: ${product.size || "—"}\n\nPlease share availability and delivery details.`;
  const waHref = number ? buildWaLink(number, waMessage) : "#";
  const callHref = number ? `tel:+${number}` : "#";

  return (
    <div data-testid="product-detail-page" className="bg-oshveda-bg">
      <div className="container-x pt-10">
        <Link to="/shop" data-testid="back-to-shop" className="inline-flex items-center gap-2 text-sm text-oshveda-muted hover:text-oshveda-accent transition-colors">
          <ArrowLeft size={14} /> Back to shop
        </Link>
      </div>
      <div className="container-x py-10 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-10 lg:gap-16">
        <div className="md:col-span-6">
          <div className="aspect-[4/5] bg-oshveda-bgAlt rounded-3xl overflow-hidden">
            <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="md:col-span-6">
          <div className="label-eyebrow mb-3">{product.category}</div>
          <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight text-oshveda-ink leading-tight mb-4">
            {product.name}
          </h1>
          <p className="text-oshveda-ink2 text-lg leading-relaxed mb-6">{product.short_description}</p>

          <div className="flex items-baseline gap-3 mb-2">
            <span className="font-serif text-4xl text-oshveda-primary">₹{product.price.toFixed(0)}</span>
            {product.compare_at_price > product.price && (
              <>
                <span className="text-lg text-oshveda-muted line-through">₹{product.compare_at_price.toFixed(0)}</span>
                <span className="text-xs tracking-widest uppercase text-oshveda-accent font-semibold">
                  Save ₹{(product.compare_at_price - product.price).toFixed(0)}
                </span>
              </>
            )}
          </div>
          {product.size && <div className="text-sm text-oshveda-muted mb-8">Pack size: <span className="text-oshveda-ink">{product.size}</span></div>}

          <div className="border-t border-oshveda-borderSubtle pt-6 mb-8">
            <div className="label-eyebrow mb-2">Order today via</div>
            <p className="text-sm text-oshveda-ink2 mb-5">Tap below to place your order on WhatsApp or call us directly. Cash on Delivery available across India.</p>
            <div className="flex flex-wrap gap-3">
              <a
                href={waHref}
                target="_blank"
                rel="noreferrer"
                data-testid="product-whatsapp-order"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-whatsapp text-white text-sm tracking-wide font-semibold hover:bg-whatsapp-hover hover:-translate-y-0.5 transition-all"
              >
                <MessageCircle size={18} /> Order on WhatsApp
              </a>
              <a
                href={callHref}
                data-testid="product-call-order"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-oshveda-primary text-oshveda-primary text-sm tracking-wide font-semibold hover:bg-oshveda-primary hover:text-oshveda-bg transition-all"
              >
                <Phone size={18} /> Call to Order
              </a>
            </div>
          </div>

          {product.benefits?.length > 0 && (
            <div className="mb-8">
              <h3 className="font-serif text-2xl text-oshveda-ink mb-4">Benefits</h3>
              <ul className="space-y-2.5">
                {product.benefits.map((b, i) => (
                  <li key={i} className="flex items-start gap-3 text-oshveda-ink2">
                    <Check size={16} className="text-oshveda-accent mt-1 flex-shrink-0" strokeWidth={2.5} />
                    <span>{b}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Description, ingredients, usage */}
      <div className="container-x pb-20 grid grid-cols-1 md:grid-cols-12 gap-10">
        <div className="md:col-span-7">
          <h3 className="font-serif text-3xl text-oshveda-ink mb-4">About this product</h3>
          <p className="text-oshveda-ink2 text-lg leading-relaxed whitespace-pre-line">{product.description}</p>
        </div>
        <div className="md:col-span-5 space-y-8">
          {product.ingredients?.length > 0 && (
            <div className="bg-oshveda-bgAlt rounded-2xl p-7">
              <div className="flex items-center gap-2 mb-3">
                <Leaf size={16} className="text-oshveda-primary" />
                <h4 className="font-serif text-xl text-oshveda-ink">Ingredients</h4>
              </div>
              <ul className="text-sm text-oshveda-ink2 space-y-1.5">
                {product.ingredients.map((ing, i) => <li key={i}>• {ing}</li>)}
              </ul>
            </div>
          )}
          {product.how_to_use && (
            <div className="bg-oshveda-bgAlt rounded-2xl p-7">
              <h4 className="font-serif text-xl text-oshveda-ink mb-2">How to use</h4>
              <p className="text-sm text-oshveda-ink2 leading-relaxed">{product.how_to_use}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
